import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  RefreshCw, Database, AlertTriangle, Link2, GitCompareArrows, Plug,
  Plus, Pencil, Eye, PowerOff, Trash2, Archive, RotateCcw,
} from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDelete, formatTs } from './ops_helpers';

const ID_RE = /^[a-z0-9]+-[a-z0-9_]+-[a-z0-9_]+-(live|demo)$/;

const EMPTY_FORM = {
  tenant_id: 'aurion', broker: '', account: '', strategy: '', env: 'live',
  symbol: '', bot_id: '', strategy_version_expected: '', terminal_data_folder: '',
  notes: '', status: 'active',
};

const driftBadge = (d) => {
  const map = {
    drift: { cls: 'bg-rose-600', label: 'DRIFT' },
    ok: { cls: 'bg-emerald-600', label: 'OK' },
    unknown: { cls: 'bg-zinc-700', label: 'sin versión esperada' },
    no_report: { cls: 'bg-amber-600', label: 'sin reporte' },
  };
  const m = map[d] || map.unknown;
  return <Badge data-testid={`fleet-drift-${d}`} className={`${m.cls} text-white`}>{m.label}</Badge>;
};

const statusBadge = (s) => {
  const cls = s === 'inactive' ? 'bg-zinc-600' : s === 'active' ? 'border-emerald-600 text-emerald-400' : '';
  return <Badge variant="outline" className={`text-xs ${cls}`} data-testid={`fleet-status-${s}`}>{s}</Badge>;
};

const originOf = (d) => {
  if (d.status === 'discovered') return 'discovered';
  if (d.source === 'auto_register') return 'auto_registered';
  return 'manual';
};

const originBadge = (d) => {
  const o = originOf(d);
  const map = {
    manual: 'border-zinc-600 text-zinc-300',
    auto_registered: 'border-sky-600 text-sky-400',
    discovered: 'bg-amber-600 text-white',
  };
  return <Badge variant={o === 'discovered' ? 'default' : 'outline'} className={`text-xs ${map[o]}`} data-testid={`fleet-origin-${o}`}>{o}</Badge>;
};

const Stat = ({ label, value, testId, color = 'text-zinc-100' }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 px-4 py-3" data-testid={testId}>
    <div className="text-xs text-zinc-500">{label}</div>
    <div className={`text-2xl font-semibold ${color}`}>{value}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 border-b border-zinc-800/50">
    <span className="text-zinc-500 text-sm">{label}</span>
    <span className="text-zinc-200 text-sm font-mono text-right break-all">{value ?? '—'}</span>
  </div>
);

// ---------- Diálogo de Crear / Editar / Vincular huérfano ----------
const DeploymentFormDialog = ({ open, mode, initial, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmRename, setConfirmRename] = useState(null); // {newId} cuando cambia identidad

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_FORM, ...(initial || {}) });
      setConfirmRename(null);
    }
  }, [open, initial]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  const previewId = useMemo(() => {
    const n = (x) => (x || '').trim().toLowerCase();
    return `${n(form.broker)}-${n(form.account)}-${n(form.strategy)}-${n(form.env)}`;
  }, [form.broker, form.account, form.strategy, form.env]);

  const idValid = ID_RE.test(previewId);
  const isEdit = mode === 'edit';
  const identityChanged = isEdit && initial?.deployment_id && previewId !== initial.deployment_id;

  const doSave = async (confirmIdChange = false) => {
    setSaving(true);
    try {
      const body = {
        tenant_id: form.tenant_id, broker: form.broker.trim().toLowerCase(),
        account: form.account.trim().toLowerCase(), strategy: form.strategy.trim().toLowerCase(),
        env: form.env, symbol: form.symbol || null, bot_id: form.bot_id || null,
        strategy_version_expected: form.strategy_version_expected || null,
        terminal_data_folder: form.terminal_data_folder || null,
        notes: form.notes || null, status: form.status,
      };
      if (isEdit) {
        await apiPut(`/api/ops/fleet/${initial.deployment_id}`, { ...body, confirm_id_change: confirmIdChange });
        toast.success(confirmIdChange ? `Renombrado a ${previewId}` : 'Deployment actualizado');
      } else {
        await apiPost('/api/ops/fleet', { ...body, deployment_id: previewId });
        toast.success(`Deployment creado: ${previewId}`);
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    if (!idValid) { toast.error('deployment_id inválido. Revisa broker/account/strategy/env.'); return; }
    if (identityChanged) { setConfirmRename({ newId: previewId }); return; }
    doSave(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl" data-testid="fleet-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {mode === 'create' && 'Crear deployment'}
              {mode === 'link' && 'Vincular huérfano'}
              {mode === 'edit' && 'Editar deployment'}
            </DialogTitle>
            <DialogDescription>
              El <code>deployment_id</code> y el <code>magic</code> se generan automáticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>tenant_id</Label>
              <Input data-testid="fleet-form-tenant" value={form.tenant_id} onChange={onInput('tenant_id')} />
            </div>
            <div>
              <Label>env <span className="text-rose-400">*</span></Label>
              <Select value={form.env} onValueChange={set('env')}>
                <SelectTrigger data-testid="fleet-form-env"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="live">live</SelectItem>
                  <SelectItem value="demo">demo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>broker <span className="text-rose-400">*</span></Label>
              <Input data-testid="fleet-form-broker" autoComplete="off" placeholder="ej. icmarkets" value={form.broker} onChange={onInput('broker')} />
            </div>
            <div>
              <Label>account <span className="text-rose-400">*</span></Label>
              <Input data-testid="fleet-form-account" autoComplete="off" placeholder="ej. 13138046" value={form.account} onChange={onInput('account')} />
            </div>
            <div>
              <Label>strategy <span className="text-rose-400">*</span></Label>
              <Input data-testid="fleet-form-strategy" autoComplete="off" placeholder="ej. sniper" value={form.strategy} onChange={onInput('strategy')} />
            </div>
            <div>
              <Label>symbol</Label>
              <Input data-testid="fleet-form-symbol" autoComplete="off" placeholder="ej. XAUUSD" value={form.symbol} onChange={onInput('symbol')} />
            </div>
            <div>
              <Label>bot_id (telemetría)</Label>
              <Input data-testid="fleet-form-botid" autoComplete="off" placeholder="ej. sniper" value={form.bot_id} onChange={onInput('bot_id')} />
            </div>
            <div>
              <Label>strategy_version_expected</Label>
              <Input data-testid="fleet-form-version" autoComplete="off" placeholder="ej. 1.0.0" value={form.strategy_version_expected} onChange={onInput('strategy_version_expected')} />
            </div>
            <div>
              <Label>terminal_data_folder (opcional)</Label>
              <Input data-testid="fleet-form-folder" autoComplete="off" placeholder="ej. 9F2415D0...FE5" value={form.terminal_data_folder} onChange={onInput('terminal_data_folder')} />
            </div>
            {isEdit && (
              <div>
                <Label>status</Label>
                <Select value={form.status} onValueChange={set('status')}>
                  <SelectTrigger data-testid="fleet-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['active', 'inactive', 'paused', 'retired'].map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2">
              <Label>notes (opcional)</Label>
              <Textarea data-testid="fleet-form-notes" rows={2} value={form.notes} onChange={onInput('notes')} />
            </div>
          </div>

          <div className="mt-2 rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm" data-testid="fleet-form-preview">
            <span className="text-zinc-500">deployment_id: </span>
            <span className={`font-mono ${idValid ? 'text-emerald-400' : 'text-rose-400'}`}>{previewId}</span>
            {!idValid && <span className="text-rose-400 ml-2">(formato inválido)</span>}
            {identityChanged && <span className="text-amber-400 ml-2">· cambiará la identidad (pedirá confirmación)</span>}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button data-testid="fleet-form-save" disabled={saving || !idValid} onClick={handleSubmit}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmRename} onOpenChange={(o) => !o && setConfirmRename(null)}>
        <AlertDialogContent data-testid="fleet-rename-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Cambiar identidad del deployment</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a renombrar <code>{initial?.deployment_id}</code> a <code>{confirmRename?.newId}</code>.
              Esto recalcula el magic y reescribe la entrada del catálogo. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="fleet-rename-confirm-btn" onClick={() => { setConfirmRename(null); doSave(true); }}>
              Sí, renombrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// ---------- Diálogo de Detalle ----------
const DetailDialog = ({ deploymentId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (deploymentId) apiGet(`/api/ops/fleet/${deploymentId}`).then(setD).catch((e) => toast.error(e.message));
  }, [deploymentId]);

  const doDelete = async () => {
    try {
      await apiDelete(`/api/ops/fleet/${deploymentId}`);
      toast.success('Deployment eliminado');
      setConfirmDelete(false); onChanged(); onClose();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={!!deploymentId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="fleet-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base break-all">{deploymentId}</DialogTitle>
          <DialogDescription>Detalle del deployment</DialogDescription>
        </DialogHeader>
        {!d ? <div className="text-zinc-500 text-sm">Cargando…</div> : (
          <div className="space-y-1">
            <Field label="tenant_id" value={d.tenant_id} />
            <Field label="broker / account" value={`${d.broker} / ${d.account}`} />
            <Field label="strategy / env" value={`${d.strategy} / ${d.env}`} />
            <Field label="symbol" value={d.symbol} />
            <Field label="magic" value={d.magic} />
            <Field label="bot_id" value={d.bot_id} />
            <Field label="terminal_data_folder" value={d.terminal_data_folder} />
            <Field label="versión esperada" value={d.strategy_version_expected} />
            <Field label="versión reportada" value={d.reported_version} />
            <div className="flex justify-between py-1 border-b border-zinc-800/50">
              <span className="text-zinc-500 text-sm">drift</span>{driftBadge(d.drift_status)}
            </div>
            <Field label="último heartbeat" value={d.last_heartbeat_ts ? formatTs(d.last_heartbeat_ts) : '—'} />
            <Field label="stale" value={d.is_stale === null ? '—' : (d.is_stale ? 'SÍ' : 'no')} />
            <Field label="estado" value={d.status} />
            <Field label="host" value={d.host} />
            <Field label="últimos errores" value={d.recent_errors ? JSON.stringify(d.recent_errors) : 'ninguno'} />
            {d.notes && <Field label="notas" value={d.notes} />}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>Cerrar</Button>
          <Button data-testid="fleet-detail-delete" variant="outline" size="sm"
            className="text-rose-400 border-rose-900/50" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="w-4 h-4 mr-1" /> Eliminar (avanzado)
          </Button>
        </DialogFooter>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent data-testid="fleet-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar deployment</AlertDialogTitle>
              <AlertDialogDescription>
                Acción destructiva e irreversible. Para desactivar sin borrar, usa «Marcar inactivo». ¿Eliminar <code>{deploymentId}</code>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction data-testid="fleet-delete-confirm-btn" onClick={doDelete}>Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

export const FleetPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState(null);     // {mode, initial}
  const [detailId, setDetailId] = useState(null);
  const [seedOpen, setSeedOpen] = useState(false);
  const [seedOverwrite, setSeedOverwrite] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [inactiveTarget, setInactiveTarget] = useState(null);
  const [runtimeFilter, setRuntimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await apiGet('/api/ops/fleet')); }
    catch (e) { toast.error(`Error cargando flota: ${e.message}`); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await apiPost(`/api/ops/fleet/${id}/approve`, {});
      toast.success(`${id} aprobado (status=active)`);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const runSeed = async () => {
    setSeeding(true);
    try {
      const res = await apiPost(`/api/ops/fleet/seed?overwrite=${seedOverwrite}`, {});
      toast.success(`Catálogo sembrado: ${res.created.length} creados, ${res.skipped.length} ya existían`);
      setSeedOpen(false); await load();
    } catch (e) { toast.error(`Error al sembrar: ${e.message}`); }
    finally { setSeeding(false); }
  };

  const markInactive = async () => {
    try {
      await apiPut(`/api/ops/fleet/${inactiveTarget}`, { status: 'inactive' });
      toast.success(`${inactiveTarget} marcado como inactivo`);
      setInactiveTarget(null); await load();
    } catch (e) { toast.error(e.message); }
  };

  const reactivate = async (id) => {
    try {
      await apiPut(`/api/ops/fleet/${id}`, { status: 'active' });
      toast.success(`${id} reactivado (status=active)`);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const s = data?.summary || {};
  const ARCHIVED_STATES = ['inactive', 'retired'];
  const allDeps = data?.deployments || [];
  const archivedDeps = allDeps.filter((d) => ARCHIVED_STATES.includes(d.status));
  const deps = allDeps.filter((d) =>
    !ARCHIVED_STATES.includes(d.status) &&
    (runtimeFilter === 'all' || (d.runtime_type || 'unknown') === runtimeFilter) &&
    (statusFilter === 'all' || d.status === statusFilter)
  );
  const orphans = data?.orphans || [];

  return (
    <div className="space-y-6" data-testid="fleet-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Database className="w-5 h-5 text-violet-400" /> Flota — Catálogo de despliegues
          </h2>
          <p className="text-sm text-zinc-500">
            Fuente de verdad del parque. Identidad <code>deployment_id</code>, versión esperada vs real (drift) y huérfanos.
          </p>
          {(s.discovered ?? 0) > 0 ? (
            <button
              data-testid="fleet-discovered-badge"
              onClick={() => setStatusFilter('discovered')}
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium px-3 py-1 transition-colors animate-pulse"
              title="Filtrar por descubrimientos pendientes"
            >
              <AlertTriangle className="w-4 h-4" /> Descubrimientos pendientes: {s.discovered}
            </button>
          ) : (
            <span data-testid="fleet-discovered-badge" className="mt-2 inline-flex items-center gap-2 text-xs text-zinc-500">
              <Plug className="w-3 h-3" /> Descubrimientos pendientes: 0
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="fleet-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="active">active</SelectItem>
              <SelectItem value="discovered">discovered</SelectItem>
              <SelectItem value="paused">paused</SelectItem>
            </SelectContent>
          </Select>
          <Select value={runtimeFilter} onValueChange={setRuntimeFilter}>
            <SelectTrigger className="w-[150px]" data-testid="fleet-runtime-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los runtime</SelectItem>
              <SelectItem value="python">python</SelectItem>
              <SelectItem value="mql5">mql5</SelectItem>
              <SelectItem value="unknown">sin runtime</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="fleet-create-btn" size="sm" onClick={() => setFormState({ mode: 'create', initial: null })}>
            <Plus className="w-4 h-4 mr-1" /> Crear deployment
          </Button>
          <Button data-testid="fleet-seed-btn" variant="outline" size="sm" onClick={() => { setSeedOverwrite(false); setSeedOpen(true); }}>
            <Database className="w-4 h-4 mr-1" /> Sembrar parque
          </Button>
          <Button data-testid="fleet-refresh-btn" variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refrescar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="fleet-summary">
        <Stat label="Despliegues" value={s.total ?? 0} testId="fleet-stat-total" />
        <Stat label="Activos" value={s.active ?? 0} testId="fleet-stat-active" color="text-emerald-400" />
        <div onClick={() => archivedDeps.length && setShowArchived((v) => !v)} className={archivedDeps.length ? 'cursor-pointer' : ''} data-testid="fleet-stat-inactive-toggle">
          <Stat label="Inactivos" value={s.inactive ?? 0} testId="fleet-stat-inactive" color={s.inactive ? 'text-zinc-300' : 'text-zinc-100'} />
        </div>
        <Stat label="Discovered" value={s.discovered ?? 0} testId="fleet-stat-discovered" color={s.discovered ? 'text-amber-400' : 'text-zinc-100'} />
        <Stat label="Con drift" value={s.drift ?? 0} testId="fleet-stat-drift" color={s.drift ? 'text-rose-400' : 'text-zinc-100'} />
        <Stat label="Stale" value={s.stale ?? 0} testId="fleet-stat-stale" color={s.stale ? 'text-amber-400' : 'text-zinc-100'} />
        <Stat label="Huérfanos" value={s.orphans ?? 0} testId="fleet-stat-orphans" color={s.orphans ? 'text-amber-400' : 'text-zinc-100'} />
      </div>

      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <GitCompareArrows className="w-4 h-4 text-violet-400" /> Despliegues
          </CardTitle>
          <CardDescription>{deps.length} unidades en el catálogo</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fleet-table">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-800">
                <th className="py-2 pr-3">deployment_id</th>
                <th className="py-2 px-3">cuenta</th>
                <th className="py-2 px-3">magic</th>
                <th className="py-2 px-3">bot_id</th>
                <th className="py-2 px-3">origen</th>
                <th className="py-2 px-3">v.esp/real</th>
                <th className="py-2 px-3">drift</th>
                <th className="py-2 px-3">heartbeat</th>
                <th className="py-2 px-3">estado</th>
                <th className="py-2 px-3 text-right">acciones</th>
              </tr>
            </thead>
            <tbody>
              {deps.map((d) => (
                <tr key={d.deployment_id} className="border-b border-zinc-800/50" data-testid={`fleet-row-${d.deployment_id}`}>
                  <td className="py-2 pr-3 font-mono text-xs text-zinc-200">{d.deployment_id}</td>
                  <td className="py-2 px-3 text-zinc-400">{d.broker}/{d.account}</td>
                  <td className="py-2 px-3 font-mono text-xs">{d.magic ?? '—'}</td>
                  <td className="py-2 px-3">
                    {d.linked
                      ? <span className="inline-flex items-center gap-1 text-emerald-400"><Link2 className="w-3 h-3" />{d.bot_id}</span>
                      : <span className="text-zinc-600">{d.bot_id || 'sin vincular'}</span>}
                  </td>
                  <td className="py-2 px-3">{originBadge(d)}</td>
                  <td className="py-2 px-3 text-zinc-400 text-xs">{d.strategy_version_expected || '—'} / {d.reported_version || '—'}</td>
                  <td className="py-2 px-3">{driftBadge(d.drift_status)}</td>
                  <td className="py-2 px-3 text-xs">
                    {d.last_heartbeat_ts
                      ? <span className={d.is_stale ? 'text-amber-400' : 'text-emerald-400'}>{d.is_stale ? 'STALE · ' : ''}{formatTs(d.last_heartbeat_ts)}</span>
                      : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="py-2 px-3">{statusBadge(d.status)}</td>
                  <td className="py-2 px-3">
                    <div className="flex justify-end gap-1">
                      {d.status === 'discovered' && (
                        <Button data-testid={`fleet-approve-${d.deployment_id}`} size="sm" variant="outline"
                          className="h-7 px-2 text-emerald-400 border-emerald-700/50" title="Aprobar discovered" onClick={() => approve(d.deployment_id)}>
                          Aprobar
                        </Button>
                      )}
                      <Button data-testid={`fleet-detail-${d.deployment_id}`} size="icon" variant="ghost" className="h-7 w-7" title="Detalle" onClick={() => setDetailId(d.deployment_id)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button data-testid={`fleet-edit-${d.deployment_id}`} size="icon" variant="ghost" className="h-7 w-7" title="Editar" onClick={() => setFormState({ mode: 'edit', initial: d })}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {d.status !== 'inactive' && (
                        <Button data-testid={`fleet-inactive-${d.deployment_id}`} size="icon" variant="ghost" className="h-7 w-7 text-amber-400" title="Marcar inactivo" onClick={() => setInactiveTarget(d.deployment_id)}>
                          <PowerOff className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {deps.length === 0 && (
                <tr><td colSpan={10} className="py-8 text-center text-zinc-500" data-testid="fleet-empty">
                  Catálogo vacío. Pulsa «Sembrar parque» o «Crear deployment».
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/60 border-zinc-800" data-testid="fleet-archived-card">
        <CardHeader className="cursor-pointer select-none" onClick={() => setShowArchived((v) => !v)}>
          <CardTitle className="text-base flex items-center gap-2">
            <Archive className="w-4 h-4 text-zinc-400" /> Archivados / Inactivos
            {archivedDeps.length > 0 && <Badge className="bg-zinc-600" data-testid="fleet-archived-count">{archivedDeps.length}</Badge>}
            <span className="ml-auto text-xs text-zinc-500" data-testid="fleet-archived-toggle">{showArchived ? 'ocultar ▲' : 'mostrar ▼'}</span>
          </CardTitle>
          <CardDescription>Despliegues desactivados o retirados. Ocultos de la lista principal.</CardDescription>
        </CardHeader>
        {showArchived && (
          <CardContent className="overflow-x-auto">
            {archivedDeps.length === 0 ? (
              <div className="text-sm text-zinc-500 flex items-center gap-2" data-testid="fleet-archived-empty">
                <Plug className="w-4 h-4" /> No hay despliegues archivados.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="fleet-archived-table">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-zinc-800">
                    <th className="py-2 pr-3">deployment_id</th>
                    <th className="py-2 px-3">cuenta</th>
                    <th className="py-2 px-3">origen</th>
                    <th className="py-2 px-3">estado</th>
                    <th className="py-2 px-3 text-right">acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedDeps.map((d) => (
                    <tr key={d.deployment_id} className="border-b border-zinc-800/50 opacity-80" data-testid={`fleet-archived-row-${d.deployment_id}`}>
                      <td className="py-2 pr-3 font-mono text-xs text-zinc-400">{d.deployment_id}</td>
                      <td className="py-2 px-3 text-zinc-500">{d.broker}/{d.account}</td>
                      <td className="py-2 px-3">{originBadge(d)}</td>
                      <td className="py-2 px-3">{statusBadge(d.status)}</td>
                      <td className="py-2 px-3">
                        <div className="flex justify-end gap-1">
                          <Button data-testid={`fleet-reactivate-${d.deployment_id}`} size="sm" variant="outline"
                            className="h-7 px-2 text-emerald-400 border-emerald-700/50" title="Reactivar (status=active)" onClick={() => reactivate(d.deployment_id)}>
                            <RotateCcw className="w-3 h-3 mr-1" /> Reactivar
                          </Button>
                          <Button data-testid={`fleet-detail-${d.deployment_id}`} size="icon" variant="ghost" className="h-7 w-7" title="Detalle" onClick={() => setDetailId(d.deployment_id)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button data-testid={`fleet-edit-${d.deployment_id}`} size="icon" variant="ghost" className="h-7 w-7" title="Editar" onClick={() => setFormState({ mode: 'edit', initial: d })}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        )}
      </Card>

      <Card className="bg-zinc-900/60 border-amber-900/40" data-testid="fleet-orphans-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Huérfanos
            {orphans.length > 0 && <Badge className="bg-amber-600">{orphans.length}</Badge>}
          </CardTitle>
          <CardDescription>Bots que envían heartbeat pero NO tienen un despliegue en el catálogo</CardDescription>
        </CardHeader>
        <CardContent>
          {orphans.length === 0 ? (
            <div className="text-sm text-emerald-400 flex items-center gap-2" data-testid="fleet-orphans-none">
              <Plug className="w-4 h-4" /> Ningún huérfano. Todos los heartbeats están catalogados.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="fleet-orphans-list">
              {orphans.map((o) => (
                <button key={o.bot_id} data-testid={`fleet-orphan-${o.bot_id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-amber-700/80 hover:bg-amber-600 text-white text-xs px-3 py-1 transition-colors"
                  onClick={() => setFormState({ mode: 'link', initial: { ...EMPTY_FORM, bot_id: o.bot_id } })}>
                  <Link2 className="w-3 h-3" /> {o.bot_id} · vincular
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diálogos */}
      {formState && (
        <DeploymentFormDialog
          open={!!formState} mode={formState.mode} initial={formState.initial}
          onClose={() => setFormState(null)} onSaved={load}
        />
      )}
      <DetailDialog deploymentId={detailId} onClose={() => setDetailId(null)} onChanged={load} />

      <AlertDialog open={seedOpen} onOpenChange={setSeedOpen}>
        <AlertDialogContent data-testid="fleet-seed-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Sembrar parque provisional</AlertDialogTitle>
            <AlertDialogDescription>
              Carga los 6 despliegues provisionales. Con <b>overwrite</b> desactivado, NO se sobreescriben los existentes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-3 py-2">
            <Switch data-testid="fleet-seed-overwrite" checked={seedOverwrite} onCheckedChange={setSeedOverwrite} />
            <span className="text-sm text-zinc-300">overwrite: <b className={seedOverwrite ? 'text-rose-400' : 'text-emerald-400'}>{seedOverwrite ? 'ACTIVADO (sobreescribe)' : 'desactivado (seguro)'}</b></span>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="fleet-seed-confirm-btn" disabled={seeding} onClick={(e) => { e.preventDefault(); runSeed(); }}>
              {seeding ? 'Sembrando…' : 'Sembrar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!inactiveTarget} onOpenChange={(o) => !o && setInactiveTarget(null)}>
        <AlertDialogContent data-testid="fleet-inactive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar como inactivo</AlertDialogTitle>
            <AlertDialogDescription>
              Se cambiará el estado de <code>{inactiveTarget}</code> a <b>inactive</b> (no se borra). ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="fleet-inactive-confirm-btn" onClick={markInactive}>Marcar inactivo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
